import {
  CSSelectOption,
  CheckboxGroup,
  CheckboxItem,
  CheckboxItemAnswer,
  Crosstab,
  CrosstabAnswerGrouping,
  CrosstabDefinition,
  CrosstabDefnCol,
  CrosstabDefnItem,
  CrosstabDefnItemMultiScore,
  CrosstabDefnItemQuestion,
  CrosstabDefnItemSegment,
  CrosstabDefnRow,
  CrosstabDragData,
  CrosstabDropContainerKind,
  CrosstabDropData,
  CrosstabExportAsyncArgs,
  CrosstabExportStatus,
  CrosstabMultiScoreComparison,
  CrosstabMultiScoreComparisonRequestArgs,
  CrosstabResults,
  CrosstabSelection,
  GetQuestionCompareTableResultsArgs,
  QuestionCompareCrosstabResponse,
  QuestionSearchResult,
  RemoteDataSimple,
  ReportResult,
  Segment,
} from "types";
import { ProfileTypes, QuestionInputType, SortTypes } from "consts";
import { buildDefaultQuestionSearchFilterValues, getQuestionTypeBadgeProps } from "utils/questions";
import { dateFilterToDateRange, emptyApiDateFilter } from "utils/dates";
import { deepEqual, generateId, insertInto } from "utils/base";
import {
  extractErrorMessageOrDefault,
  getAppliedFiltersLabel,
  parseNumberOrDefault,
  weightingSchemeUtils,
} from "utils";
import { processMultiScoreRowComparison, processQuestionRowComparison } from "utils/crosstab/crosstabTranform";

import { CheckboxGroupsAPI } from "api";
import { envConfig } from "config";

type MapCrosstabDefinitionCallbackArgs = {
  row: CrosstabDefnRow;
  rowIndex: number;
  col: CrosstabDefnCol;
  colIndex: number;
  flatArrayIndex: number;
};

type MatchDefnItemArgs<T> = {
  question: (x: CrosstabDefnItemQuestion) => T;
  segment: (x: CrosstabDefnItemSegment) => T;
  multiscore: (x: CrosstabDefnItemMultiScore) => T;
};

/**
 * Pattern matches against the provided `CrosstabDefnItem` to invoke a more specific function per type.
 */
export const matchDefnItem = <T>(item: CrosstabDefnItem, args: MatchDefnItemArgs<T>): T => {
  switch (item.itemType) {
    case "question":
      return args.question(item);
    case "segment":
      return args.segment(item);
    case "multiscore":
      return args.multiscore(item);
  }
};

type MatchDefnColArgs<T> = {
  question: (x: CrosstabDefnItemQuestion) => T;
  segment: (x: CrosstabDefnItemSegment) => T;
};

/**
 * Pattern matches against the provided `CrosstabDefnCol` to invoke a more specific function per type.
 */
export const matchDefnCol = <T>(item: CrosstabDefnCol, args: MatchDefnColArgs<T>): T => {
  switch (item.itemType) {
    case "question":
      return args.question(item);
    case "segment":
      return args.segment(item);
  }
};

/**
 * Creates the custom data for the "row" or "column" drop container.
 */
export const makeCrosstabDropData = (kind: CrosstabDropContainerKind): CrosstabDropData => ({
  kind,
});

/**
 * Creates the custom data for a question result draggable instance.
 */
export const makeQuestionResultDndData = (question: QuestionSearchResult): CrosstabDragData => ({
  kind: "search-result-item",
  id: generateId(),
  itemType: "question",
  dragText: question.text,
  validDropTargets: ["column", "row"],
  questionId: question.askableId.toString(),
  questionText: question.text,
  answerChoices: question.answerChoices?.map((x) => ({
    answerChoiceId: x.id,
    answerChoiceText: x.text,
    label: "",
  })),
  answerGrouping: null,
  questionType: question.type,
});

/**
 * Creates the custom data for a segment result draggable instance.
 *
 * See `CrosstabDefinitionItem` and the `itemType` property.
 */
export const makeSegmentResultDndData = (segment: Segment): CrosstabDragData => ({
  kind: "search-result-item",
  id: generateId(),
  itemType: "segment",
  dragText: segment.name,
  validDropTargets: ["column"],
  segmentUuid: segment.uuid,
  segmentName: segment.name,
  questionId: ProfileTypes.GENDER.qid.toString(),
  answerGrouping: {
    answerGroupingUri: envConfig.genderCombinedAnswerGrouping,
  },
});
/**
 * Creates the custom data for a segment result draggable instance.
 *
 * See `CrosstabDefinitionItem` and the `itemType` property.
 */
export const makeMultiScoreResultDndData = (multiscore: ReportResult): CrosstabDragData => ({
  kind: "search-result-item",
  id: generateId(),
  multiscoreId: multiscore.id,
  multiscoreName: multiscore.name,
  itemType: "multiscore",
  dragText: multiscore.name,
  validDropTargets: ["row"],
});

/**
 * Creates `CrosstabSelection` from a CrosstabDefinitionItem.
 * This shape is ultimately what we store in the component state to represent selected items in rows/columns.
 *
 * Note: We populate this state of selected items in a few ways
 * - When a user drags a question result to a row or column
 * - On initial page load when we hydrate from any selections loaded from the API
 */
export const makeCrosstabSelection = (data: CrosstabDefnItem): CrosstabSelection => ({
  ...data,
  id: generateId(),
});

/**
 * Returns a string label for the provided `CrosstabDefnItem`.
 */
export const getDefnItemLabel = (item: CrosstabDefnItem): string =>
  matchDefnItem(item, {
    question: (x) => x.label || x.questionText,
    segment: (x) => x.label || x.segmentName || "",
    multiscore: (x) => x.label || x.multiscoreName,
  });

export const getDefnItemDropTargets = (item: CrosstabDefnItem): CrosstabDropContainerKind[] =>
  matchDefnItem(item, {
    question: () => ["column", "row"],
    segment: () => ["column"],
    multiscore: () => ["row"],
  });

/**
 * Creates the custom data for a "sortable" row item or column item.
 */
export const makeSortableDndData = (
  item: CrosstabSelection,
  dropType: CrosstabDropContainerKind,
): CrosstabDragData => ({
  ...item,
  kind: dropType === "row" ? "row-item" : "col-item",
  dragText: getDefnItemLabel(item),
  validDropTargets: getDefnItemDropTargets(item),
});

/**
 * Creates a clone of CrosstabSelection applying a new ID to the clone.
 */
export const cloneCrosstabSelection = (item: CrosstabSelection): CrosstabSelection => ({
  ...structuredClone(item),
  id: generateId(),
});

/**
 * Creates a clone of each item in `cloneIds` within the `items`.
 * The result is a new array of items with each item cloned.
 */
export const cloneCrosstabSelections = (items: CrosstabSelection[], cloneIds: string[]): CrosstabSelection[] => {
  return cloneIds.reduce((acc, currentId) => {
    const itemToClone = acc.find((x) => x.id === currentId);
    return itemToClone ? insertInto(acc, cloneCrosstabSelection(itemToClone), (x) => x?.id === currentId) : acc;
  }, items);
};

/**
 * Maps over both the rows and cols of a Crosstab Definition to produce a flattened array.
 * Your callback will receive the current row, col, indexes, and the index of the flat array.
 *
 * This is useful when creating an array of all combinations of rows/cols.
 * Specifically, we use this to produce a list of question compare queries to load crosstab data.
 */
export const mapCrosstabDefinition = <T>(
  defn: CrosstabDefinition,
  mapFn: (args: MapCrosstabDefinitionCallbackArgs) => T,
): T[] =>
  defn.rows.flatMap((row, rowIndex) =>
    defn.cols.map((col, colIndex) =>
      mapFn({ row, rowIndex, col, colIndex, flatArrayIndex: rowIndex * defn.cols.length + colIndex }),
    ),
  );

/**
 * Returns a string "title" for the crosstab column header.
 * This will prioritize the custom label if provided
 * and if not fall back to other data depending on the `itemType`.
 */
export const getCrosstabResultsHeader = (col: CrosstabDefnCol): string => {
  return col.label || (col.itemType === "segment" ? col.segmentName || "" : col.questionText);
};

/**
 * Transforms a "flat" array of question compare queries into `CrosstabResults`.
 * Performs 2 main functions:
 *
 * 1. Determine the overall loading state of the Crosstab data.
 *    Generally, we display loading until the first "row" of question compares are fully loaded.
 *    From there we render full rows of question compares as they finish loading
 * 2. Structure the response data in a way that is easier for the UI to render.
 *
 * The general approach is to process the data at the "row" level.
 * In this context, a "row" is a list of question compare responses.
 * For example, if a Crosstab is 2x2 then we will have produced 4 question compare queries.
 * We call queries at index 0 and 1 row 1 and queries at index 2 and 3 row 2.
 *
 * [0,1,2,3]     -> how we _requested_ the data via `useQueries`
 * [[0,1],[2,3]] -> how we are _processing_ the data as "rows"
 *
 * See `processCrosstabRow`.
 */
export const transformCrosstabResults = (
  definition: CrosstabDefinition,
  queries: RemoteDataSimple<QuestionCompareCrosstabResponse | CrosstabMultiScoreComparison>[],
): CrosstabResults => {
  // if any of the question compare queries in the first row fail, then consider the entire crosstab failed.
  const firstRowResults = queries.slice(0, definition.cols.length);
  if (firstRowResults.some((x) => !!x.error)) {
    const messages = firstRowResults.map((x) => {
      if (x.error) return extractErrorMessageOrDefault(x.error);
    });
    return { kind: "error", message: messages.join("\n") };
  }

  // if any of the question compare queries in the first row do NOT have data, then show a loader
  if (firstRowResults.some((x) => !x.data)) {
    return { kind: "loading" };
  }

  // at least the entire first row of question compares has successfully loaded - signal the UI to start rendering
  // the `rows` property below will track the remaining loading/error/success state of the table.
  const totalColCount = firstRowResults.reduce(
    (acc, result) => (result.data?.results?.colSummary?.length ?? 1) + acc + 1,
    0,
  );
  return {
    kind: "success",
    table: {
      totalColCount,
      tableHeaders: definition.cols.map((col, i) => ({
        colCount: queries[i]?.data?.results?.colSummary?.length ?? 1,
        supportsRowTotal: (queries[i]?.data?.results?.colSummary?.length ?? 1) > 1,
        title: getCrosstabResultsHeader(col),
        filters: queries[i]?.data?.filters || {},
      })),
      // we visit ALL of the rows in the definition
      // some may not be loaded yet and that is OK
      dataRowGroups: definition.rows.map((rowDefn, rowIndex) => {
        const startIndex = rowIndex * definition.cols.length;
        const endIndex = rowIndex * definition.cols.length + definition.cols.length;
        const currentRow = queries.slice(startIndex, endIndex);

        if (currentRow.some((x) => x.error)) {
          return [{ kind: "error", message: currentRow.map((x) => extractErrorMessageOrDefault(x.error)).join("\n") }];
        }

        if (currentRow.some((x) => !x.data)) {
          return [{ kind: "loading" }];
        }

        if (rowDefn.itemType === "multiscore") {
          return processMultiScoreRowComparison({
            colDefns: definition.cols,
            rowDefn,
            rowData: currentRow.map((x) => x.data) as CrosstabMultiScoreComparison[],
          });
        }

        return processQuestionRowComparison({
          colDefns: definition.cols,
          rowDefn: rowDefn as CrosstabDefnItemQuestion,
          rowData: currentRow.map((x) => x.data) as QuestionCompareCrosstabResponse[],
        });
      }),
    },
  };
};

/**
 * Provides a consistent shape of CrosstabAnswerGrouping for equality comparison purposes.
 *
 * Note: The user only ever edits the answer grouping's `answerGroupingUri` - via selection in the UI.
 * As a result, the only thing we care about for comparison purposes is the `answerGroupingUri`.
 * If two items have the same `answerGroupingUri` then they are considered equal.
 */
const normalizeAnswerGroupingForCompare = (
  answerGrouping: CrosstabAnswerGrouping | null | undefined,
): CrosstabAnswerGrouping | undefined =>
  answerGrouping
    ? {
        answerGroupingText: "",
        answerGroupingUri: answerGrouping.answerGroupingUri,
      }
    : undefined;

const normalizeDefnItemSegment = ({
  itemType,
  questionId,
  dateFilter,
  label,
  segmentUuid,
  network,
  weightingSchemeName,
}: CrosstabDefnItemSegment): Omit<CrosstabDefnItemSegment, "id" | "answerGrouping"> => ({
  itemType,
  questionId: questionId?.toString() || "",
  dateFilter: dateFilter || { ...emptyApiDateFilter(), noDateFilter: true },
  label: label || undefined,
  segmentUuid: segmentUuid || undefined,
  network: network || undefined,
  weightingSchemeName: weightingSchemeName || undefined,
});

const normalizeDefnItemQuestion = ({
  itemType,
  answerChoices,
  questionId,
  answerGrouping,
  dateFilter,
  label,
  segmentUuid,
  network,
  weightingSchemeName,
}: CrosstabDefnItemQuestion): Omit<CrosstabDefnItemQuestion, "id" | "questionText"> => ({
  itemType,
  answerChoices,
  questionId: questionId?.toString() || "",
  answerGrouping: normalizeAnswerGroupingForCompare(answerGrouping),
  dateFilter: dateFilter || { ...emptyApiDateFilter(), noDateFilter: true },
  label: label || undefined,
  segmentUuid: segmentUuid || undefined,
  network: network || undefined,
  weightingSchemeName: weightingSchemeName || undefined,
});

const normalizeDefnItemMultiScore = ({
  itemType,
  multiscoreId,
  label,
}: CrosstabDefnItemMultiScore): Omit<CrosstabDefnItemMultiScore, "id" | "multiscoreName" | "segmentName"> => ({
  itemType,
  multiscoreId: multiscoreId.toString() || "",
  label: label || undefined,
});

/**
 * This allows us to more accurately compare crosstab definitions for unsaved changes.
 * We specifically extract only the data we care about and ensure we get optional fields
 * to consistent values.
 * For example, the `label` could be `null` or `undefined` or `''`
 * and we want to make sure those are all mapped to a consistent value for comparison.
 */
const normalizeDefinitionItemForCompare = (item: CrosstabDefnItem): unknown =>
  matchDefnItem<unknown>(item, {
    question: normalizeDefnItemQuestion,
    segment: normalizeDefnItemSegment,
    multiscore: normalizeDefnItemMultiScore,
  });

/**
 * Returns `true` if the provided `CrosstabDefinition` instances should be considered equal.
 * Otherwise returns `false`.
 *
 * This is used to determine if a Crosstab has unsaved changes.
 */
export const areCrosstabDefinitionsEqual = (defn1: CrosstabDefinition, defn2: CrosstabDefinition): boolean =>
  deepEqual(
    {
      rows: defn1.rows.map(normalizeDefinitionItemForCompare),
      cols: defn1.cols.map(normalizeDefinitionItemForCompare),
    },
    {
      rows: defn2.rows.map(normalizeDefinitionItemForCompare),
      cols: defn2.cols.map(normalizeDefinitionItemForCompare),
    },
  );

/**
 * Returns `true` if the answer choice is the "Yes" option - otherwise `false`.
 */
const isCheckboxYesAnswerChoice = (choice: CheckboxItemAnswer): boolean => /yes/i.test(choice.answerChoice);

/**
 * Builds a `CrosstabSelection` for the the given `CheckboxItem`.
 * The goal is to pre-select the "Yes" option by default.
 * If we can't find a "Yes" option then we will select the CheckboxGroup's checkedAnswer.
 */
const getCheckboxSelection = (group: CheckboxGroup, item: CheckboxItem) => {
  const yesAnswer = [item.uncheckedAnswer, item.checkedAnswer].find(isCheckboxYesAnswerChoice) ?? item.checkedAnswer;
  return makeCrosstabSelection({
    id: generateId(),
    itemType: "question",
    questionId: item.questionRefid.toString(),
    questionText: item.qtext,
    /**
     * NOTE: for checkbox questions we default a custom label on creation.
     * The qtext usually includes/repeats the checkbox qtext itself and the label is shorter.
     * For example:
     * Checkbox Text:  What best describes your current employment status? (Please select all that apply)
     * Question Text:  [CHECKBOX 1560] Do you currently have a full-time job?
     * Label:          Full-time job
     */
    label: item.label,
    answerChoices: [
      {
        answerChoiceId: yesAnswer.answerChoiceId,
        answerChoiceText: yesAnswer.answerChoice || "",
        label: "",
      },
    ],
    questionType: "checkbox",
    /**
     * Necessary props for the "checkbox item" badge
     */
    qtype: "radio",
    checkboxGroup: [
      {
        groupId: group.groupId,
        groupText: group.groupText,
        itemText: item.label,
      },
    ],
  });
};

/**
 * Builds the list of `CrosstabSelection` instances to include when a user drag/drops a checkbox item.
 * A checkbox item is a wrapper for several questions (each with a yes/no answer option).
 * See `getCheckboxSelection` for how each individual question is mapped.
 */
export const getCheckboxItems = (checkboxQuestionId: string): Promise<CrosstabSelection[]> => {
  const groupId = parseNumberOrDefault(checkboxQuestionId, 0);
  return CheckboxGroupsAPI.get(Number(groupId))
    .then((x) => x.checkboxItems.map((item) => getCheckboxSelection({ ...x, groupId }, item)))
    .catch(() => []);
};

type IsBorderCellXArgs = {
  group: unknown[];
  groupIndex: number;
  cells: unknown[];
  cellIndex: number;
  supportsTotal: boolean;
  showTotalRow: boolean;
};

/**
 * Helper to determine if a cell sits on the border of the **next CrosstabComparison**.
 * Also considers the "Row Total" column which may not be present
 * either because it is not supported for that comparison OR the user has toggled it off.
 *
 * See the tests for some examples.
 */
export const isBorderCellX = (args: IsBorderCellXArgs): boolean => {
  return (
    !(args.showTotalRow && args.supportsTotal) &&
    args.cellIndex === args.cells.length - 1 &&
    args.groupIndex !== args.group.length - 1
  );
};

/**
 * Returns a label which should be used for the weighting scheme.
 */
const getWeightingSchemeLabelName = (
  item: CrosstabDefnItem,
  containerKind: CrosstabDropContainerKind,
): string | null => {
  if (
    item.itemType === "multiscore" ||
    containerKind === "row" ||
    weightingSchemeUtils.isUnweighted(item.weightingSchemeName)
  ) {
    return null;
  }

  return item.weightingSchemeLabel || "Account Default";
};

export const getCrosstabSelectionFilterLabel = (
  item: CrosstabDefnItem,
  containerKind: CrosstabDropContainerKind,
  hideNetworkText = false,
): string => {
  // TODO: See if `getAppliedFiltersLabel` can just take straight dates
  const { startDate, endDate } = dateFilterToDateRange(item.itemType === "multiscore" ? null : item.dateFilter);
  const filters = getAppliedFiltersLabel({
    networkName: item.itemType === "multiscore" ? null : (item.networkName ?? null),
    segmentName: item.itemType === "question" ? (item.segmentName ?? null) : null,
    targetName: null,
    weightingSchemeLabel: getWeightingSchemeLabelName(item, containerKind),
    startDate: startDate ? startDate.toISOString() : null,
    endDate: endDate ? endDate.toISOString() : null,
    hideNetworkText,
  });

  return `All responses ${filters}`;
};

/**
 * Creates the request args needed for a MultiScore Row Comparison
 * based on the provided `row` and `col` definition items.
 */
export const getCrosstabMultiScoreComparisonRequestArgs = (
  row: CrosstabDefnItemMultiScore,
  col: CrosstabDefnCol,
  wscheme: string | null,
): CrosstabMultiScoreComparisonRequestArgs =>
  matchDefnCol<CrosstabMultiScoreComparisonRequestArgs>(col, {
    question: (x) => ({
      multiscoreId: row.multiscoreId,
      qid: x.questionId,
      segment: x.segmentUuid,
      aids: x.answerChoices?.length > 0 ? x.answerChoices.map((x) => x.answerChoiceId.toString()) : undefined,
      vgUri: x.answerGrouping?.answerGroupingUri,
      network: x.network,
      wscheme,
      ...dateFilterToDateRange(x.dateFilter),
    }),
    segment: (x) => ({
      multiscoreId: row.multiscoreId,
      segment: x.segmentUuid,
      network: x.network,
      wscheme,
      ...dateFilterToDateRange(x.dateFilter),
    }),
  });

/**
 * Creates the request args needed for a Question Row Comparison
 * based on the provided `row` and `col` definition items.
 */
export const getCrosstabQuestionComparisonRequestArgs = (
  row: CrosstabDefnItemQuestion,
  col: CrosstabDefnCol,
  weightingSchemeName: string | null,
): GetQuestionCompareTableResultsArgs =>
  matchDefnCol<GetQuestionCompareTableResultsArgs>(col, {
    question: (qCol) => ({
      qid: row.questionId,
      cmpQid: qCol.questionId,
      answerIdsMainQuestion: row.answerChoices.map((x) => x.answerChoiceId),
      answerIdsComparisonQuestion: qCol.answerChoices.map((x) => x.answerChoiceId),
      valueGroupingUri: row.answerGrouping?.answerGroupingUri,
      secondaryValueGroupingUri: qCol.answerGrouping?.answerGroupingUri,
      segments: [row.segmentUuid, qCol.segmentUuid],
      network: qCol.network,
      weightingSchemeName,
      ...dateFilterToDateRange(qCol.dateFilter),
    }),
    segment: (sCol) => ({
      qid: row.questionId,
      cmpQid: ProfileTypes.GENDER.qid.toString(),
      answerIdsMainQuestion: row.answerChoices.map((x) => x.answerChoiceId),
      answerIdsComparisonQuestion: [],
      valueGroupingUri: row.answerGrouping?.answerGroupingUri,
      secondaryValueGroupingUri: envConfig.genderCombinedAnswerGrouping,
      segments: [row.segmentUuid, sCol.segmentUuid],
      network: sCol.network,
      weightingSchemeName,
      ...dateFilterToDateRange(sCol.dateFilter),
    }),
  });

type CrosstabRowsAndColsNotSelectedProps = CrosstabDefinition;
export const crosstabRowsAndColsNotSelected = ({ rows = [], cols = [] }: CrosstabRowsAndColsNotSelectedProps) => {
  return !cols.length || !rows.length;
};

/**
 * Returns a string representation of the `CrosstabExportStatus`.
 */
export const getCrosstabExportStatusDisplay = (status: CrosstabExportStatus): string => {
  switch (status) {
    case "completed":
      return "Ready to download";
    case "failed":
      return "Failed to export";
    case "pending":
      return "Pending";
    case "active":
      return "Processing";
    case "revoked":
      return "Cancelled";
  }
};

export const makeCrosstabExportAsyncArgs = (
  overrides: Partial<CrosstabExportAsyncArgs> = {},
): CrosstabExportAsyncArgs => ({
  name: "",
  rowPercent: true,
  colPercent: true,
  percentFromExp: true,
  count: true,
  rowTotal: true,
  colTotal: true,
  ...overrides,
});

/**
 * Returns an array of the sub headers used in the cross tab preview.
 */
export const getCrosstabPreviewSubHeaders = (crosstab: CrosstabDefinition): string[] => {
  return crosstab.cols.flatMap((col) => {
    if (col.itemType === "segment") {
      return [col.segmentName || ""];
    }

    if (col.answerGrouping) {
      return [col.answerGrouping.label || col.answerGrouping.answerGroupingText || ""];
    }

    return col.answerChoices.map((x) => x.label || x.answerChoiceText);
  });
};

/**
 * Returns a bool indicating if the crosstab can be exported.
 */
export const canExportCrosstab = ({ defn }: Crosstab): boolean => {
  return defn.cols.length > 0 && defn.rows.length > 0;
};

/**
 * Gets the props for a CrosstabItemBadge for the provided `CrosstabSelection`.
 */
export const getCrosstabItemBadgeProps = (item: CrosstabSelection) => {
  if (item.itemType === "multiscore" || item.itemType === "segment") {
    return item.itemType;
  }

  return getQuestionTypeBadgeProps(item);
};

/**
 * Options for the Input Type filter in the QuestionList for Crosstab.
 * We specifically exclude "Matrix", but may want to support it in the future.
 */
export const inputTypeGroupOptionsCrosstab: CSSelectOption[] = [
  { text: "Radio", value: QuestionInputType.Radio },
  { text: "Checkbox", value: QuestionInputType.Checkbox },
];

export const getCrosstabDefaultQuestionSearchFilters = (isInternal: boolean) => ({
  ...buildDefaultQuestionSearchFilterValues(isInternal),
  sortTerm: SortTypes.RELEVANCE,
  inputType: [QuestionInputType.Radio, QuestionInputType.Checkbox],
});
